// DISP-6 — fetch + print-choice dialog + document, mounted imperatively by printDispatchSlip.js (so the order page,
// the Dispatches page and the Record-Dispatch success state can all open the same BL without owning any state).
// Reads only: GET /sales-dispatches/:id (slip) or GET /sales-orders/:op + /items + /partners (D10 copy). Posts nothing.
import ShellModal from '../../shell/drawer/ShellModal';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import DispatchSlipDocument from './DispatchSlipDocument';
import { slipModel, deliveredCopyModel } from './dispatchSlipModel.mjs';

async function load({ slipId, operationId }) {
  if (slipId) return { slip: await api.request(`/sales-dispatches/${encodeURIComponent(slipId)}`) };
  const [order, items, partners] = await Promise.all([api.getSalesOrder(operationId), api.getItems(), api.request('/partners')]);
  return { order, items, partner: (partners || []).find((p) => p.id === order?.header?.partner_id) || null };
}

export default function DispatchSlipPrintHost({ slipId, operationId, showPrices, onDone }) {
  const { t: tr } = useTranslation('dispatchSlip');
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [prices, setPrices] = useState(!!showPrices);
  const [phase, setPhase] = useState(typeof showPrices === 'boolean' ? 'doc' : 'ask');   // a caller that already decided skips the dialog
  const ref = useRef(null);

  useEffect(() => {
    let alive = true;
    load({ slipId, operationId }).then((d) => { if (alive) setData(d); }).catch((e) => { if (alive) setError(e.message || String(e)); });
    return () => { alive = false; };
  }, [slipId, operationId]);

  const model = useMemo(() => {
    if (!data) return null;
    return data.slip ? slipModel(data.slip, { showPrices: prices }) : deliveredCopyModel(data.order, data.items, data.partner, { showPrices: prices });
  }, [data, prices]);

  if (phase === 'doc') {
    if (error) return <ShellModal title={tr('dialog.title')} size="sm" onClose={onDone} closeLabel={tr('dialog.close')}><p role="alert" style={{ color: 'var(--st-crit)', margin: 0 }}>{error}</p></ShellModal>;
    return <DispatchSlipDocument model={model} onClose={onDone} docId={slipId || operationId} />;
  }
  return (
    <ShellModal title={tr('dialog.title')} size="sm" onClose={onDone} closeLabel={tr('dialog.close')}
      primary={<button type="button" ref={ref} className="btn success" data-testid="bl-preview" disabled={!model} onClick={() => setPhase('doc')}>{model ? tr('dialog.preview') : tr('loading')}</button>}>
      <p style={{ margin: '0 0 12px', color: 'var(--text-2)' }}>{tr('dialog.body')}</p>
      <label style={{ display: 'flex', gap: 8, alignItems: 'center', margin: '0 0 16px', cursor: 'pointer' }}>
        <input type="checkbox" data-testid="bl-show-prices" checked={prices} onChange={(e) => setPrices(e.target.checked)} /> {tr('dialog.showPrices')}
      </label>
      {error && <p role="alert" style={{ color: 'var(--st-crit)', margin: 0 }}>{error}</p>}
    </ShellModal>
  );
}
